import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Account } from '../types';
import PriceTag from './PriceTag';
import RankBadge from './RankBadge';
import TierCounter from './TierCounter';
import CollectorTierBadge from './CollectorTierBadge';
import { hapticImpact } from '../lib/telegram';
import { useCatalogStore } from '../store/catalogStore';

const STATUS_LABEL: Record<string, string> = {
  RESERVED: 'Забронирован',
  SOLD: 'Продан',
};

export default function AccountCard({
  account,
  favorite,
  onToggleFavorite,
}: {
  account: Account;
  favorite?: boolean;
  onToggleFavorite?: (id: string) => void;
}) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const cover = account.screenshots[0]?.url ?? null;

  const collabChips = useMemo(() => {
    const seen = new Map<string, string>();
    for (const s of account.skins) {
      if (s.collabName) seen.set(s.collab!, s.collabName);
    }
    return [...seen.values()];
  }, [account.skins]);

  const visibleChips = collabChips.slice(0, 3);
  const extraCount = collabChips.length - visibleChips.length;
  const statusLabel = STATUS_LABEL[account.status];
  const rankLabel = useCatalogStore((s) => s.meta?.ranks.find((r) => r.id === account.rank)?.name) ?? account.rank;

  return (
    <Link
      to={`/account/${account.id}`}
      onClick={() => hapticImpact('light')}
      className="block overflow-hidden rounded-2xl border border-mlbb-border bg-mlbb-card"
    >
      <div className="relative h-40 w-full bg-mlbb-border/60">
        {cover ? (
          <img
            src={cover}
            alt=""
            loading="lazy"
            onLoad={() => setImgLoaded(true)}
            className={`h-full w-full object-cover transition-opacity duration-300 ${
              imgLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-4xl opacity-30">🎮</div>
        )}
        {onToggleFavorite && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              hapticImpact('light');
              onToggleFavorite(account.id);
            }}
            className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-lg backdrop-blur-sm"
          >
            {favorite ? '❤️' : '🤍'}
          </button>
        )}
        {statusLabel && (
          <div className="absolute left-2 top-2 rounded-full bg-black/70 px-2.5 py-1 text-xs font-medium text-white">
            {statusLabel}
          </div>
        )}
      </div>

      <div className="space-y-1.5 p-3">
        <div className="flex items-center justify-between">
          <PriceTag price={account.price} currency={account.currency} />
          <RankBadge rankId={account.rank} label={rankLabel} />
        </div>
        <CollectorTierBadge tierId={account.collectorTier} level={account.collectorTierLevel} />
        <TierCounter legend={account.legendCount} grand={account.grandCount} collector={account.collectorCount} />
        {visibleChips.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {visibleChips.map((name) => (
              <span key={name} className="rounded-full bg-mlbb-border px-2 py-0.5 text-[11px] text-white/70">
                {name}
              </span>
            ))}
            {extraCount > 0 && (
              <span className="rounded-full bg-mlbb-border px-2 py-0.5 text-[11px] text-white/50">
                +{extraCount}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
